import {
  PROVINCE_ONTARIO,
  PROVINCE_ALBERTA,
  PROVINCE_NEW_BRUNSWICK,
  PROVINCE_NOVA_SCOTIA,
} from 'variables/constants';

export type ProvinceCode =
  | typeof PROVINCE_ONTARIO
  | typeof PROVINCE_ALBERTA
  | typeof PROVINCE_NEW_BRUNSWICK
  | typeof PROVINCE_NOVA_SCOTIA;

export const PROVINCE_CODES = {
  ONTARIO: PROVINCE_ONTARIO,
  ALBERTA: PROVINCE_ALBERTA,
  NEW_BRUNSWICK: PROVINCE_NEW_BRUNSWICK,
  NOVA_SCOTIA: PROVINCE_NOVA_SCOTIA,
} as const;

type CoverageAvailability = {
  [K in ProvinceCode]: boolean;
};

type CoverageAvailabilityMap = Record<string, CoverageAvailability>;

export const PROVINCE_COVERAGE_AVAILABILITY: CoverageAvailabilityMap = {
  // Core coverages - available in all provinces
  liability_limit: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  all_perils: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  collision: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  comprehensive: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },

  dcpd_deductible: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },

  // Endorsements with province restrictions
  opcf_2: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: false,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_6a_passengers: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_8: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_8a: {
    [PROVINCE_ONTARIO]: false,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_4_min: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: false,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: false,
  },
  opcf_19a: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_20: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_20_disappearing: {
    [PROVINCE_ONTARIO]: false,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_27: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_35: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_43: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_44: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  opcf_48: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: false,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: false,
  },
  opcf_49: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: false,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: false,
  },

  // Other endorsements - available in all provinces
  accident_waiver: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  minor_conviction_protection: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  carsharing: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  waiver_of_deductible: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: true,
    [PROVINCE_NOVA_SCOTIA]: true,
  },

  // Ontario SABS Accident Benefits - Ontario only
  accident_benefits: {
    [PROVINCE_ONTARIO]: true,
    [PROVINCE_ALBERTA]: false,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: false,
  },

  // SEF33/SEF48 - Nova Scotia and Alberta only
  sef33_pedestrian_coverage: {
    [PROVINCE_ONTARIO]: false,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: true,
  },
  sef48_supplementary_benefits: {
    [PROVINCE_ONTARIO]: false,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: true,
  },

  sef_13c: {
    [PROVINCE_ONTARIO]: false,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: false,
  },
  sef_13d: {
    [PROVINCE_ONTARIO]: false,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: false,
  },
  sef_49a: {
    [PROVINCE_ONTARIO]: false,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: false,
  },
  cae_17: {
    [PROVINCE_ONTARIO]: false,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: false,
  },
  cae_18: {
    [PROVINCE_ONTARIO]: false,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: false,
  },
  sef_13h: {
    [PROVINCE_ONTARIO]: false,
    [PROVINCE_ALBERTA]: true,
    [PROVINCE_NEW_BRUNSWICK]: false,
    [PROVINCE_NOVA_SCOTIA]: false,
  },
};

export const isCoverageAvailable = (
  coverageKey: string,
  provinceCode: number,
): boolean => {
  const availability = PROVINCE_COVERAGE_AVAILABILITY[coverageKey];
  if (!availability) {
    return true;
  }
  return availability[provinceCode as ProvinceCode] ?? true;
};

export const getUnavailableCoveragesForProvince = (
  provinceCode: number,
): string[] => {
  return Object.entries(PROVINCE_COVERAGE_AVAILABILITY)
    .filter(([, availability]) => !availability[provinceCode as ProvinceCode])
    .map(([key]) => key);
};

export const isOntario = (provinceCode: number): boolean =>
  provinceCode === PROVINCE_ONTARIO;

export const hasAccidentBenefits = (provinceCode: number): boolean =>
  provinceCode === PROVINCE_ONTARIO;

export const hasSef33Sef48 = (provinceCode: number): boolean =>
  provinceCode === PROVINCE_NOVA_SCOTIA || provinceCode === PROVINCE_ALBERTA;

export const getUnderwritingApprovalThreshold = (
  provinceCode: number,
): number => {
  if (provinceCode === PROVINCE_NOVA_SCOTIA) return 500000;
  return 1000000;
};

type EndorsementLabels = {
  [K in ProvinceCode]?: string;
};

const ENDORSEMENT_LABELS: Record<string, EndorsementLabels> = {
  opcf_6a_passengers: {
    [PROVINCE_ONTARIO]: 'OPCF 6a Permission to Carry Paying Passengers',
    [PROVINCE_ALBERTA]: '6a Carry Passengers for Compensation or Hire',
    [PROVINCE_NEW_BRUNSWICK]:
      '6a Permission to Carry Passengers for Compensation',
    [PROVINCE_NOVA_SCOTIA]: '6a Carry Passengers for Compensation or Hire',
  },
  opcf_8: {
    [PROVINCE_ONTARIO]: 'OPCF 8 Property Damage Reimbursement',
    [PROVINCE_ALBERTA]: '8 Property Damage Reimbursement Endorsement',
    [PROVINCE_NOVA_SCOTIA]: '8 Property Damage Reimbursement',
  },
  opcf_8a: {
    [PROVINCE_ALBERTA]:
      '8a Named Person Property Damage Reimbursement Endorsement',
    [PROVINCE_NEW_BRUNSWICK]:
      '8a Named Person Property Damage Reimbursement Endorsement',
    [PROVINCE_NOVA_SCOTIA]:
      '8a Named Person Property Damage Reimbursement Endorsement',
  },
  opcf_20_disappearing: {
    [PROVINCE_ALBERTA]:
      '26 Disappearing Deductible Endorsement (All Perils or Collision or Upset)',
    [PROVINCE_NEW_BRUNSWICK]: '26 Disappearing Deductible',
    [PROVINCE_NOVA_SCOTIA]: '26 Disappearing Deductible',
  },
};

export const getEndorsementLabel = (
  endorsementKey: string,
  provinceCode: number,
  fallbackLabel: string,
): string => {
  const labels = ENDORSEMENT_LABELS[endorsementKey];
  if (!labels) return fallbackLabel;
  return labels[provinceCode as ProvinceCode] ?? fallbackLabel;
};

export const hasOpcf6aDescribedUse = (provinceCode: number): boolean =>
  provinceCode !== PROVINCE_NEW_BRUNSWICK;
